/*
    HubSchema.js -- Device Cloud Hub Schema
 */

import MetricSchema from '../dev-schemas/parts/MetricSchema.json5'
import WidgetSchema from '../dev-schemas/parts/WidgetSchema.json5'

const Range = {
    anchor: {type: 'string', enum: ['absolute', 'relative'], default: 'relative', notes: 'Anchor type to be "absolute" or "relative"'},
    start: {type: 'date', notes: 'Start date'},
    period: {type: 'number', default: 28 * 86400, notes: 'Time period in seconds'},
    refresh: {type: 'number', default: 60, notes: 'Refresh rate in seconds'},
    override: {type: 'boolean', notes: 'Override the dashboard user selected time range'},
    timezone: {type: 'string', enum: ['local', 'utc'], default: 'local', notes: 'Timezone to display the data in'},
}

const HubSchema = {
    version: 'latest',
    format: 'onetable:1.1.0',
    params: {
        isoDates: true,
        nulls: false,
        timestamps: false,
        typeField: '_type',
    },
    indexes: {
        primary: {
            hash: 'pk',
            sort: 'sk',
            description: 'Primary index',
        },
        gs1: {
            hash: 'gs1pk',
            sort: 'gs1sk',
            description: 'General secondary index',
            project: 'all',
        },
    },
    process: {},
    models: {

        Account: {
            pk: {type: 'string', value: 'h#${cloudId}'},
            sk: {type: 'string', value: 'a#${id}'},
            gs1pk: {type: 'string', value: 'a#${email}'},
            gs1sk: {type: 'string', value: 'a#${id}${cloudId}'},

            activity: {type: 'date', notes: 'Last activity date'},
            closed: {type: 'boolean', default: false, notes: 'Set to true when account closed'},
            cloudId: {type: 'string', required: true, notes: 'Owning device cloud ID', fixed: true},
            created: {type: 'date', notes: 'Date account created'},
            email: {type: 'string', required: true, notes: 'Account email'},
            id: {type: 'string', generate: 'ulid', notes: 'Unique account ID', fixed: true},
            name: {type: 'string', required: true, notes: 'Account or company name'},
            suspended: {type: 'boolean', role: 'support', private: true, notes: 'Set to true if account temporarily suspended'},
        },

        Action: {
            pk:             { type: 'string', value: 'h#${cloudId}' },
            sk:             { type: 'string', value: 'action#${id}' },

            gs1pk:          { type: 'string', value: 'action#' },
            gs1sk:          { type: 'string', value: 'action#${due}#${id}' },

            /*
                TODO - should have subschema
                Args for the action:
                account (event bus)
                accountId
                command
                days
                delay
                deviceId
                dimensions
                expires
                headers
                method
                message
                model
                subject
                time
             */
            args:           { type: 'object', default: {}, notes: 'Action args'},
            buffer:         { type: 'object', default: {}, notes: 'Buffer optimizations for metrics' },
            cloudId:        { type: 'string', required: true, notes: 'Owning device cloud ID', fixed: true},
            /*
                User context to parameterize:
                    properties: {} for matching database items
                    model,
                    namespace, metric, dimensions, statistic, period, 
             */
            context:        { type: 'object', default: {}, notes: 'Trigger expression context, merged with user params' },
            description:    { type: 'string', notes: 'Action description' },
            destination:    { type: 'string', notes: 'URL, email address, function name, topic, phone number' },
            due:            { type: 'number', default: 0, notes: 'When the action is next due' },
            enable:         { type: 'boolean', default: true, notes: 'Enable or disable the action' },
            error:          { type: 'string', notes: 'Error message from last invocation' },
            expression:     { type: 'string', notes: 'Trigger expression' },
            format:         { type: 'string', enum: ['html', 'json', 'text'], notes: 'Message format' },
            id:             { type: 'string', generate: 'ulid', notes: 'Unique action ID', fixed: true},
            name:           { type: 'string', required: true, unique: true, notes: 'Action name' },
            poll:           { type: 'number', default: 0, notes: 'Delay between pollng the trigger' },
            rearm:          { type: 'number', default: 0, notes: 'Time period to suppress triggering' },
            severity:       { type: 'string', enum: ['info', 'warning', 'error', 'critical'], notes: 'Alert severity' },
            trigger:        { type: 'string', 
                                enum: ['database', 'eventbridge', 'http', 'metric', 'mqtt', 'schedule', 'sync', 'user'],
                                notes: 'Source of action trigger' 
                            },
            type:           { type: 'string', required: true,
                                enum: ['alert', 'builder-alert', 'builder-metric', 'database', 
                                'delay', 'device', 'email', 'eventbridge', 'http', 'lambda', 'metric', 
                                'mqtt', 'sms' ],
                                notes: 'Action type'
                            },
        },

        Alert: {
            pk:             { type: 'string', value: 'a#${accountId}' },
            sk:             { type: 'string', value: 'alert#${id}' },

            gs1pk:          { type: 'string', value: 'alert#' },
            gs1sk:          { type: 'string', value: 'alert#${cloudId}#${name}#${seq}#${id}' },

            accountId:      { type: 'string', required: true, notes: 'Owning account ID', fixed: true },
            cloudId:        { type: 'string', required: true, notes: 'Owning device cloud ID', fixed: true },
            context:        { type: 'object', notes: 'Alert context' },
            count:          { type: 'number', default: 0, notes: 'Count of alerts for this sequence' },
            id:             { type: 'string', required: true, generate: 'ulid', notes: 'Unique alert ID', fixed: true },
            message:        { type: 'string', required: true, notes: 'Text message regarding alert' },
            name:           { type: 'string', required: true, notes: 'Action name generating the alert' },
            seq:            { type: 'string', required: true, notes: 'Unique sequence string for the alert type' },
            severity:       { type: 'string', required: true, notes: 'Alert severity' },
            subject:        { type: 'string', required: true, notes: 'Alert subject' },
            resolved:       { type: 'boolean', default: false, notes: 'Set to true when alert is resolved' },
            timestamp:      { type: 'date', required: true, notes: 'When the alert was created' },
        },

        Dashboard: {
            pk: {type: 'string', value: 'a#${accountId}'},
            sk: {type: 'string', value: 'dashboard#${id}'},

            gs1pk: {type: 'string', value: 'dashboard#'},
            gs1sk: {type: 'string', value: 'dashboard#${id}'},

            accountId: {type: 'string', required: true, notes: 'Owning account ID', fixed: true},
            css: {type: 'array', default: [], partial: false, notes: 'CSS styles for the dashboard'},
            emulate: {type: 'string', notes: 'Emulate device type when designing'},
            fixed: {type: 'boolean', default: true, notes: 'Prevent runtime changes to the dashboard'},
            framed: {type: 'boolean', default: true, notes: 'Show a frame around dashboard widgets'},
            id: {type: 'string', generate: 'ulid', notes: 'Unique dashboard ID', fixed: true},
            layout: {type: 'string', default: 'grid', notes: 'Dashboard layout'},
            live: {type: 'boolean', default: true, notes: 'Enable live data updates'},
            name: {type: 'string', required: true, notes: 'Dashboard name'},
            range: {type: 'object', default: {}, partial: false, schema: Range, notes: 'Time range for the dashboard'},
            refresh: {type: 'number', notes: 'Refresh rate in seconds'},
            toolbar: {type: 'boolean', default: true, notes: 'Show a toolbar on the dashboard'},
            type: {type: 'string', notes: 'Dashboard type', fixed: true},
            widgetCss: {type: 'array', default: [], partial: false, notes: 'CSS styles for dashboard widgets'},

            //  Completed by blend below
            widgets: {},

        },

        Device: {
            pk: {type: 'string', value: 'a#${accountId}'},
            sk: {type: 'string', value: 'd#${id}'},

            gs1pk: {type: 'string', value: 'd#${id}'},
            gs1sk: {type: 'string', value: 'd#'},
            accessed: {type: 'date', map: 'ac', notes: 'When the device was last accessed'},
            accountId: {type: 'string', required: true, encode: ['pk', '#', 1], notes: 'Owning manager account ID'},
            certArn: {type: 'string', map: 'ce', private: true},
            cloudId: {type: 'string', required: true, map: 'cl', private: true},
            created: {type: 'date', map: 'cr', notes: 'When the device was first claimed'},
            id: {type: 'string', required: true, encode: ['sk', '#', 1], notes: 'Device Claim ID'},
            product: {type: 'string', map: 'pr', notes: 'Builder Product name'},
            productId: {type: 'string', map: 'pi', notes: 'Builder Product ID', fixed: true},
            test: {type: 'boolean', map: 'te', notes: 'Device is for test purposes', fixed: true},
            version: {type: 'string', map: 've', notes: 'Firmware version'},
            checked: {type: 'string', map: 'ch', notes: 'Checked version'},
        },

        Hub: {
            pk: {type: 'string', value: 'h#${id}'},
            sk: {type: 'string', value: 'h#'},

            gs1pk: {type: 'string', value: 'h#'},
            gs1sk: {type: 'string', value: 'h#${id}'},

            api: {type: 'string', notes: 'Hub (Device cloud) API endpoint', fixed: true},
            apiGateway: {type: 'string', notes: 'Manager API Gateway ID', fixed: true},
            builderId: {type: 'string', notes: 'Builder accountId', fixed: true},
            connected: {type: 'boolean', notes: 'Set to true when the Hub is active'},
            deviceRole: {type: 'string', default: 'IotoDeviceAccess', notes: 'IAM role for keys minted for device use'},
            devices: {type: 'number', default: 0, notes: 'Number of devices connected to the Hub'},
            enable: {type: 'boolean', notes: 'Set to true when the Hub is enabled'},
            host: { type: 'string', notes: 'Hub host name'},
            id: {type: 'string', notes: 'Same as builder cloudId', fixed: true},
            iotPolicy: {type: 'string', default: 'IotoThingPolicy', notes: 'IAM policy for keys minted for device use'},
            load: {type: 'number', default: 0, notes: 'Hub load'},
            name: {type: 'string', required: true, notes: 'Hub name'},
            open: {type: 'boolean', notes: 'Set to true when the Hub is open'},
            provisioned: {type: 'date', notes: 'When the Hub was provisioned'},
            quota: {type: 'object', notes: 'Hub quota'},
            region: {type: 'string', required: true, notes: 'Hub region', fixed: true},
            shadows: {type: 'boolean', default: false, notes: 'Set to true when IoT device shadows are enabled'},
            startPeriod: {type: 'date', notes: 'When the Hub started'},
            suspended: {type: 'boolean', notes: 'Set to true when the Hub is suspended'},
            sync: {type: 'boolean', default: false, notes: 'Set to true when the Hub supports DynamoDB sync'},
            tenants: {type: 'number', default: 0, notes: 'Number of tenants connected to the Hub'},
            type: {type: 'string', default: 'hosted', notes: 'Hub type'},
            userPoolId: {type: 'string', notes: 'Cognito user pool ID', fixed: true},
            userPoolClient: {type: 'string', notes: 'Cognito user pool Client ID', fixed: true},
        },

        Manager: {
            pk: {type: 'string', value: 'h#${cloudId}'},
            sk: {type: 'string', value: 'm#${id}'},

            gs1pk: {type: 'string', value: 'm#${domain}'},
            gs1sk: {type: 'string', value: 'm#${id}'},

            accountId: {type: 'string', notes: 'Owning account ID', fixed: true},
            certArn: {type: 'string', notes: 'Certificate ARN'},
            cloudFront: {type: 'string', notes: 'CloudFront distribution ID'},
            cloudId: {type: 'string', required: true, notes: 'Device cloud ID', fixed: true},
            custom: {type: 'boolean', default: false, notes: 'Custom domain'},
            distributionId: {type: 'string', notes: 'CloudFront distribution ID'},
            domain: {type: 'string', required: true, notes: 'Manager domain', fixed: true},
            error: {type: 'string', notes: 'Error message'},
            id: {type: 'string', generate: 'ulid', notes: 'Manager ID', fixed: true},
            name: {type: 'string', required: true, notes: 'Manager name'},
            owner: {type: 'string', notes: 'Owner account ID'},
            priorDomain: {type: 'string', notes: 'Prior domain'},
            provisioned: {type: 'date', notes: 'When the manager was provisioned'},
            region: {type: 'string', required: true, notes: 'Region', fixed: true},
            title: {type: 'string', notes: 'Manager title'},
            type: {type: 'string', hint: 'Copy of cloud.type', notes: 'Manager type', fixed: true},
            users: {type: 'array', notes: 'User allow list'},
            version: {type: 'string', notes: 'App version'},
        },

        Software: {
            pk: {type: 'string', value: 'h#${cloudId}'},
            sk: {type: 'string', value: 's#${productId}#${version}#${id}'},

            checksum: {type: 'string', notes: 'Software image checksum'},
            cloudId: {type: 'string', required: true, notes: 'Device cloud ID', fixed: true},
            description: {type: 'string', notes: 'Software description'},
            id: {type: 'string', notes: 'Software ID', fixed: true},
            enable: {type: 'boolean', default: true, notes: 'Enable or disable the software release'},
            image: {type: 'string', required: true, notes: 'Software image'},
            productId: {type: 'string', required: true, notes: 'Builder product ID', fixed: true},
            policy: {type: 'string', notes: 'Software distribution policy'},
            size: {type: 'number', notes: 'Software size in bytes'},
            version: {type: 'string', required: true, notes: 'Software version'},

            //  These two are not (yet) used
            failed: {type: 'number', default: 0, notes: 'Number of failed updates'},
            success: {type: 'number', default: 0, notes: 'Number of successful updates'},

            //  Gradual rollout state
            applied: {type: 'number', default: 0, notes: 'Number of devices that have applied the software'},
            total: {type: 'number', default: 0, notes: 'Total number of updated devices'},
            limit: {type: 'number', default: 0, notes: 'Number of devices that will be updated in this rate period'},
            percentage: {type: 'number', default: 100, notes: 'Percentage of devices that will be updated'},

            count: {type: 'number', default: 0, notes: 'Updates performed in this rate period'},
            rate: {type: 'number', default: 0, notes: 'Number of updates per period'},
            period: {type: 'number', default: 3600, notes: 'Rate period in seconds'},
            rateEnd: {type: 'date', notes: 'When the rating period will complete'},
        },

        Token: {
            pk: {type: 'string', value: 'h#${cloudId}'},
            sk: {type: 'string', value: 't#${type}#${owner}#${id}'},

            gs1pk: {type: 'string', value: 't#${id}', notes: 'Used by router to authenticate'},
            gs1sk: {type: 'string', value: 't#'},

            cloudId: {type: 'string', required: true, encode: ['pk', '#', 1], reference: 'Hub:primary:id=cloudId', fixed: true},

            type: {type: 'string', required: true, encode: ['sk', '#', 1], notes: 'Token type', fixed: true},
            owner: {type: 'string', required: true, encode: ['sk', '#', 2], notes: 'Token owner', fixed: true},
            id: {type: 'string', required: true, generate: 'ulid', encode: ['sk', '#', 3], notes: 'Token ID', fixed: true},

            description: {type: 'string', map: 'de', notes: 'Token description'},
            enable: {type: 'boolean', map: 'en', default: true, notes: 'Enable or disable the token'},
            expires: {type: 'date', map: 'ex', ttl: true, notes: 'When the token will expire'},
            role: {type: 'string', map: 'ro', required: true, notes: 'Token role'},
        },

        User: {
            pk: {type: 'string', value: 'a#${accountId}'},
            sk: {type: 'string', value: 'u#${id}'},

            gs1pk: {type: 'string', value: 'user#${email}'},
            gs1sk: {type: 'string', value: 'user#${cloudId}'},

            accountId: {type: 'string', required: true, notes: 'Owning account ID', fixed: true},
            activity: {type: 'date', notes: 'Last activity date'},
            cloudId: {type: 'string', required: true, reference: 'Hub:primary:id=cloudId', notes: 'Device cloud ID', fixed: true},
            email: {type: 'string', required: true, notes: 'User email address'},
            first: {type: 'string', notes: 'First name'},
            id: {type: 'string', generate: 'ulid', notes: 'User ID', fixed: true},
            invite: {type: 'string', private: true, notes: 'Invitation ID'},
            last: {type: 'string', notes: 'Surname'},
            role: {type: 'string', required: true, notes: 'Authorized role'},
            state: {type: 'string', enum: ['accepted', 'invited'], private: true, notes: 'Invited state'},
        },
    },
}

HubSchema.models.Metric = MetricSchema.models.Metric
HubSchema.models.Dashboard.widgets = WidgetSchema

export default HubSchema
